import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { BarChart3, Clock3, ReceiptText, TrendingUp, Wallet } from "lucide-react";
import { ChatLayoutWrapper } from "../ai-chat";
import { buildCompanyContext } from "./company/buildCompanyContext";
import { CashFlowChart } from "./company/CashFlowChart";
import { exportCompanyReport } from "./company/exportCompanyReport";
import { MonthlyPnL } from "./company/MonthlyPnL";
import type { CompanyData, CompanyLogisticsReport, FixedCost } from "./company/types";
import { formatCurrency, formatCurrencyCompact } from "./company/utils";
import type { Period } from "../funnel-detail/types";
import { formatDateForApi } from "../funnel-detail/periods";
import {
  financeService,
  getCompanyFinanceSummary,
  getFinanceLogisticsOverview,
  saveFinanceFixedCosts,
  type FinanceMetaCampaignPerformance,
  type FinanceLogisticsOverview,
} from "../../services/financeService";

type CompanyTabProps = {
  period: Period;
  onRegisterExport?: (handler: (() => void) | null) => void;
};

const periodFromSelector = (period: Period) => ({
  id: period.id,
  label: period.label,
  from: formatDateForApi(period.from),
  to: formatDateForApi(period.to),
  days: period.days,
});

const buildLogisticsReport = (overview: FinanceLogisticsOverview | null): CompanyLogisticsReport | undefined => {
  if (!overview) return undefined;
  const totalOrders = Number(overview.summary.total_orders ?? 0);
  const sections = (overview.status_breakdown ?? []).map((section) => ({
    id: section.id,
    label: section.label,
    count: Number(section.count ?? 0),
    value: Number(section.value ?? 0),
    percentage: totalOrders > 0 ? (Number(section.count ?? 0) / totalOrders) * 100 : 0,
  }));

  return {
    connected: overview.dropi_connected,
    currency: overview.summary.currency ?? "COP",
    totalOrders,
    matchedOrders: Number(overview.summary.matched_orders ?? 0),
    unmatchedOrders: Number(overview.summary.unmatched_orders ?? 0),
    matchedValue: Number(overview.summary.matched_value ?? 0),
    unmatchedValue: Number(overview.summary.unmatched_value ?? 0),
    productCost: Number(overview.summary.product_cost ?? 0),
    shippingCost: Number(overview.summary.shipping_cost ?? 0),
    variableCost: Number(overview.summary.variable_cost ?? 0),
    sections,
  };
};

export const CompanyTab = ({ period, onRegisterExport }: CompanyTabProps) => {
  const [baseData, setBaseData] = useState<CompanyData | null>(null);
  const [logisticsOverview, setLogisticsOverview] = useState<FinanceLogisticsOverview | null>(null);
  const [adCampaigns, setAdCampaigns] = useState<FinanceMetaCampaignPerformance[]>([]);
  const [fixedCosts, setFixedCosts] = useState<FixedCost[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoadError(null);
    Promise.all([
      getCompanyFinanceSummary(period),
      getFinanceLogisticsOverview(period),
      financeService.getFinanceCampaignPerformance(period).catch(() => []),
    ])
      .then(([summary, logistics, campaigns]) => {
        if (!mounted) return;
        setBaseData(summary);
        setLogisticsOverview(logistics);
        setAdCampaigns(campaigns);
        setFixedCosts(summary.fixedCosts);
      })
      .catch(() => {
        if (!mounted) return;
        setBaseData(null);
        setLogisticsOverview(null);
        setAdCampaigns([]);
        setFixedCosts([]);
        setLoadError("No pudimos cargar el resumen de tu empresa.");
      });
    return () => {
      mounted = false;
    };
  }, [period]);

  const data: CompanyData = useMemo(() => {
    if (!baseData) {
      return {
        period: periodFromSelector(period),
        netProfit: 0,
        operatingProfit: 0,
        avgRoas: 0,
        avgCpa: 0,
        totalRevenue: 0,
        grossRevenue: 0,
        collectedRevenue: 0,
        totalVariableCosts: 0,
        totalLogisticsCosts: 0,
        totalProductCosts: 0,
        totalShippingCosts: 0,
        totalFulfillmentCosts: 0,
        totalPlatformCommission: 0,
        totalReturnCosts: 0,
        pendingRevenue: 0,
        pendingProductCosts: 0,
        pendingLogisticsCosts: 0,
        estimatedPendingProfit: 0,
        estimatedTotalProfit: 0,
        totalAdInvestment: 0,
        matchedAdInvestment: 0,
        unassignedAdInvestment: 0,
        fixedCosts: [],
        fixedCostsCurrency: "COP",
        totalFixedCosts: 0,
        revenueByPaymentMethod: {
          total: 0,
          cashOnDelivery: 0,
          prepaid: 0,
          other: 0,
          collected: 0,
        },
        funnels: [],
        stages: {
          checkoutViews: 0,
          ordersCreated: 0,
          dispatched: 0,
          delivered: 0,
          returned: 0,
          ordersValue: 0,
          dispatchedValue: 0,
          deliveredValue: 0,
          returnedValue: 0,
        },
        dailyData: [],
        cpaTargets: {
          realCpa: 0,
          targetCpa20: 0,
          breakEvenCpa: 0,
        },
        avgMaxProfitableCpa: 0,
      };
    }
    const fixedCostPeriodFactor = period.days / 30;
    const totalFixedCosts = fixedCosts.reduce((acc, cost) => acc + cost.amount, 0) * fixedCostPeriodFactor;
    const netProfit = baseData.operatingProfit - totalFixedCosts;
    const estimatedTotalProfit = netProfit + (baseData.estimatedPendingProfit ?? 0);
    return {
      ...baseData,
      period: baseData.period ?? periodFromSelector(period),
      fixedCosts,
      totalFixedCosts,
      netProfit,
      estimatedTotalProfit,
      logisticsReport: buildLogisticsReport(logisticsOverview),
    };
  }, [baseData, fixedCosts, logisticsOverview, period]);

  const chatContext = useMemo(() => ({
    ...buildCompanyContext(data),
    period: {
      id: period.id,
      label: period.label,
      from: period.from.toISOString().slice(0, 10),
      to: period.to.toISOString().slice(0, 10),
      days: period.days,
    },
  }), [data, period]);

  const handleFixedCostsChange = async (costs: FixedCost[]) => {
    const currency = data.fixedCostsCurrency ?? "COP";
    setFixedCosts(costs.map((cost) => ({ ...cost, currency: cost.currency ?? currency })));
    try {
      const savedCosts = await saveFinanceFixedCosts(costs, currency);
      setFixedCosts(savedCosts);
    } catch {
      toast.error("No pudimos guardar los costos fijos.");
    }
  };

  const handleExportReport = useCallback(() => {
    const opened = exportCompanyReport(data);
    if (!opened) toast.error("No pudimos abrir el informe para exportar.");
  }, [data]);

  useEffect(() => {
    onRegisterExport?.(handleExportReport);
    return () => onRegisterExport?.(null);
  }, [handleExportReport, onRegisterExport]);

  return (
    <ChatLayoutWrapper context={chatContext}>
      <div className="space-y-6">
        {!baseData && !loadError ? (
          <div className="rounded-lg border border-gray-100 bg-white px-5 py-4 text-sm font-medium text-gray-500">
            Cargando resumen de tu empresa...
          </div>
        ) : loadError ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-5 py-4 text-sm font-medium text-red-700">
            {loadError}
          </div>
        ) : (
          <>
            <CompanySummaryCards data={data} />
            <div id="finance-company-pyg-target">
              <MonthlyPnL data={data} onFixedCostsChange={handleFixedCostsChange} adCampaigns={adCampaigns} />
            </div>
            <CashFlowChart
              period={period}
              dailyData={data.dailyData}
              costBreakdown={{
                totalProductCosts: data.totalProductCosts,
                totalShippingCosts: data.totalShippingCosts,
                totalLogisticsCosts: data.totalLogisticsCosts,
                totalVariableCosts: data.totalVariableCosts,
              }}
            />
            <CompanyReceivables data={data} />
          </>
        )}
      </div>
    </ChatLayoutWrapper>
  );
};

function CompanyReceivables({ data }: { data: CompanyData }) {
  const pendingRevenue = data.pendingRevenue ?? 0;
  const pendingCosts = (data.pendingProductCosts ?? 0) + (data.pendingLogisticsCosts ?? 0);
  const pendingProfit = data.estimatedPendingProfit ?? pendingRevenue - pendingCosts;
  const estimatedTotalProfit = data.estimatedTotalProfit ?? data.netProfit + pendingProfit;
  const collectedRevenue = data.collectedRevenue ?? data.totalRevenue;
  const totalPipeline = collectedRevenue + pendingRevenue;
  const pendingShare = totalPipeline > 0 ? (pendingRevenue / totalPipeline) * 100 : 0;
  const collectedShare = totalPipeline > 0 ? (collectedRevenue / totalPipeline) * 100 : 0;

  const metrics = [
    {
      label: "Recaudo pendiente",
      value: pendingRevenue,
      detail: `${pendingShare.toFixed(1)}% de la cartera total`,
    },
    {
      label: "Costos pendientes",
      value: pendingCosts,
      detail: "Producto, envío y flete por reconocer",
    },
    {
      label: "Utilidad pendiente",
      value: pendingProfit,
      detail: "Utilidad estimada antes de recaudo",
      negative: pendingProfit < 0,
    },
  ];

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-2 border-b border-gray-100 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Cartera pendiente</h2>
          <p className="mt-1 text-sm text-gray-500">
            Transacciones que todavía no entran al recaudo, con su utilidad proyectada.
          </p>
        </div>
        <div className="inline-flex h-10 items-center gap-2 self-start rounded-full border border-blue-100 bg-blue-50 px-4 text-sm font-semibold text-[#2b5ce6]">
          <Clock3 className="h-4 w-4" />
          Utilidad estimada {formatCurrency(estimatedTotalProfit)}
        </div>
      </div>

      <div className="px-6 py-5">
        <div className="mb-5 h-3 overflow-hidden rounded-full bg-gray-100">
          <div className="flex h-full w-full">
            <div
              className="bg-[#2b5ce6]"
              style={{ width: `${Math.min(Math.max(collectedShare, 0), 100)}%` }}
            />
            <div
              className="bg-[#67c6b3]"
              style={{ width: `${Math.min(Math.max(pendingShare, 0), 100)}%` }}
            />
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          {metrics.map((metric) => (
            <div key={metric.label} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-4">
              <p className="text-[11px] font-semibold uppercase text-gray-500">{metric.label}</p>
              <p className={`mt-2 text-xl font-bold tabular-nums ${metric.negative ? "text-red-700" : "text-gray-900"}`}>
                {formatCurrency(metric.value)}
              </p>
              <p className="mt-1 text-xs text-gray-500">{metric.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompanySummaryCards({ data }: { data: CompanyData }) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    const node = gridRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      setGridWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const compactValues = gridWidth > 0 && gridWidth < 1120;
  const formatCardCurrency = (value: number) =>
    compactValues && Math.abs(value) >= 1_000_000 ? formatCurrencyCompact(value) : formatCurrency(value);
  const collectedRevenue = data.collectedRevenue ?? data.totalRevenue;
  const margin = collectedRevenue > 0 ? (data.netProfit / collectedRevenue) * 100 : 0;
  const totalCosts = Math.max(collectedRevenue - data.netProfit, 0);
  const estimatedProfit = data.estimatedTotalProfit ?? data.netProfit + (data.estimatedPendingProfit ?? 0);
  const pendingProfit = data.estimatedPendingProfit ?? 0;
  const cards = [
    {
      label: "Recaudo total",
      value: formatCardCurrency(collectedRevenue),
      fullValue: formatCurrency(collectedRevenue),
      detail: `${data.stages.delivered} entregas reconocidas`,
      icon: Wallet,
    },
    {
      label: "Costos totales",
      value: formatCardCurrency(totalCosts),
      fullValue: formatCurrency(totalCosts),
      detail: "Variables, pauta y costos fijos",
      icon: ReceiptText,
    },
    {
      label: "Utilidad neta",
      value: formatCardCurrency(data.netProfit),
      fullValue: formatCurrency(data.netProfit),
      detail: `${margin.toFixed(1)}% margen neto`,
      icon: TrendingUp,
      negative: data.netProfit < 0,
    },
    {
      label: "Utilidad estimada",
      value: formatCardCurrency(estimatedProfit),
      fullValue: formatCurrency(estimatedProfit),
      detail: `${formatCardCurrency(pendingProfit)} pendiente`,
      icon: BarChart3,
      negative: estimatedProfit < 0,
    },
  ];

  return (
    <div
      ref={gridRef}
      id="finance-company-summary-target"
      className="grid grid-cols-1 gap-4 sm:[grid-template-columns:repeat(auto-fit,minmax(min(210px,100%),1fr))]"
    >
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="min-w-0 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase text-gray-500">{card.label}</p>
              <Icon className="h-4 w-4 text-gray-400" />
            </div>
            <p
              className={`mt-2 truncate text-2xl font-bold tabular-nums ${card.negative ? "text-red-700" : "text-gray-900"}`}
              title={card.fullValue}
            >
              {card.value}
            </p>
            <p className="mt-1 text-xs text-gray-500">{card.detail}</p>
          </div>
        );
      })}
    </div>
  );
}

export default CompanyTab;
